import {
  createFileRoute,
  redirect,
  useRouteContext,
  useRouter,
  useSearch,
} from "@tanstack/react-router";
import { z } from "zod";

import { graphql } from "@repo/graphql-types";
import {
  GetPeriodosGarantidosRecorrentesDoUsuarioDocument,
  ReservedSchedulesDetailsQueryDocument,
} from "@repo/graphql-types/graphql";
import Left from "@repo/icons/default/Left";
import { CpsSpinner } from "corpus";

import { ReservedScheduleCancelFailDrawer } from "@/components/reserved-schedules/reserved-schedule-cancel-fail-drawer";
import { ReservedScheduleCancelSimulationInfo } from "@/components/reserved-schedules/reserved-schedule-cancel-simulation-info";
import { ReservedScheduleCancelSuccessDrawer } from "@/components/reserved-schedules/reserved-schedule-cancel-success-drawer";
import { ReservedScheduleTermsOfUseUrl } from "@/components/reserved-schedules/reserved-schedule-terms-of-use-url";
import { AsyncDataWrapper } from "@/components/ui/async-data-wrapper";
import { Button } from "@/components/ui/button/button";
import {
  HeaderButton,
  HeaderRoot,
  HeaderTitle,
} from "@/components/ui/layout/header/header";
import { Page } from "@/components/ui/layout/page/page";
import {
  ensureQueryData,
  useGraphQL,
  useGraphQLMutation,
  useInvalidateQuery,
} from "@/hooks/use-graphql";
import { convertDateTimeIgnoringTimezone, formatWithZonedDate } from "@/lib/helpers/date";
import { trackEvent } from "@/lib/tracking";
import { logError } from "@/lib/utils/log-error";

import type { JSX } from "react";

const reservedScheduleIndividualCancelPageSearchParamsSchema = z.object({
  success: z.boolean().optional(),
  fail: z.boolean().optional(),
});

const ReservedScheduleIndividualCancelPageQuery = graphql(/* GraphQL */ `
  query ReservedScheduleIndividualCancelPageQuery(
    $codPeriodoGarantidoRecorrente: Int!
    $codPeriodoGarantido: Int!
  ) {
    periodoGarantidoRecorrente: tbPeriodosGarantidosRecorrentes_by_pk(
      codPeriodoGarantidoRecorrente: $codPeriodoGarantidoRecorrente
    ) {
      ativo
      codUsuarioAgenda
      dataInicio
      horaInicio
      horaFim
      diaDaSemana
      Unidade {
        nomeLimpo
        codUnidade
      }
    }
    periodoGarantido: tbPeriodosGarantidos_by_pk(
      codPeriodoGarantido: $codPeriodoGarantido
    ) {
      ativo
      codPeriodoGarantido
      codModalidadePeriodoGarantido
      Agendamento {
        data
        codAgendamento
      }
    }
  }
`);

// biome-ignore lint/plugin/route-file-exports: predates the rule; move it out
export const ReservedScheduleIndividualCancelSimulationQuery = graphql(/* GraphQL */ `
  query ReservedScheduleIndividualCancelSimulationQuery($codPeriodoGarantido: Int!) {
    LivanceApiSimulaCobrancaDeCancelamento(
      arg1: { codPeriodoGarantido: $codPeriodoGarantido, dataReferencia: null }
    ) {
      taxa
      valor
    }
  }
`);

// biome-ignore lint/plugin/route-file-exports: predates the rule; move it out
export const CancelIndividualReservedScheduleMutation = graphql(/* GraphQL */ `
  mutation CancelIndividualReservedScheduleMutation($codPeriodoGarantido: Int!) {
    LivanceApiCancelaPeriodoGarantido(
      arg1: { codPeriodoGarantido: $codPeriodoGarantido }
    ) {
      message
      success
    }
  }
`);

const ReservedScheduleIndividualCancelPage = (): JSX.Element => {
  const router = useRouter();
  const { reservedScheduleId, nextReserveId } = Route.useParams();

  const { user } = useRouteContext({ from: "__root__" });

  const params = useSearch({
    from: "/settings/reserved-schedules/$reservedScheduleId/$nextReserveId/cancel",
  });

  const queryResult = useGraphQL(ReservedScheduleIndividualCancelPageQuery, {
    codPeriodoGarantidoRecorrente: reservedScheduleId,
    codPeriodoGarantido: nextReserveId,
  });

  const { data } = queryResult;

  const reservedSchedule = data?.periodoGarantidoRecorrente;
  const nextSchedule = data?.periodoGarantido;
  // Agenda aberta junto com o PG (KRA-447): cancelar a ocorrência fecha o horário do dia (KRA-454).
  const hasLinkedSchedule = typeof reservedSchedule?.codUsuarioAgenda === "number";

  const queryResultCancelSimulation = useGraphQL(
    ReservedScheduleIndividualCancelSimulationQuery,
    {
      codPeriodoGarantido: nextReserveId,
    },
    {
      enabled: Boolean(nextSchedule?.Agendamento.data),
    },
  );

  const { data: dataCancelSimulation } = queryResultCancelSimulation;
  const simulationTax =
    dataCancelSimulation?.LivanceApiSimulaCobrancaDeCancelamento?.taxa ?? 0;
  const simulationCost =
    dataCancelSimulation?.LivanceApiSimulaCobrancaDeCancelamento?.valor ?? 0;

  const { mutateAsync, isPending } = useGraphQLMutation(
    CancelIndividualReservedScheduleMutation,
  );

  const invalidateReservedScheduleListQuery = useInvalidateQuery(
    GetPeriodosGarantidosRecorrentesDoUsuarioDocument,
  );
  const invalidateReservedSchedulesDetailsQuery = useInvalidateQuery(
    ReservedSchedulesDetailsQueryDocument,
  );

  const handleReservedScheduleCancelAsync = async (): Promise<void> => {
    const variables = {
      codPeriodoGarantido: nextReserveId,
    };

    const onSuccess = (): void => {
      trackEvent("Periodo Garantido Pontual Cancelado", {
        codUsuario: user.codUsuario,
        codPeriodoGarantidoRecorrente: reservedScheduleId,
        codPeriodoGarantido: nextReserveId,
        codAgendamento: nextSchedule?.Agendamento.codAgendamento,
        codUnidade: reservedSchedule?.Unidade.codUnidade,
        data: formatWithZonedDate(
          convertDateTimeIgnoringTimezone(nextSchedule?.Agendamento.data ?? " "),
          "dd/MM/yyyy",
        ),
      });

      void router.navigate({
        to: ".",
        search: {
          success: true,
        },
      });

      invalidateReservedScheduleListQuery();
      invalidateReservedSchedulesDetailsQuery();
    };

    const onError = (error: Error): void => {
      logError(error, variables);
      void router.navigate({
        to: ".",
        search: {
          fail: true,
        },
      });
    };

    await mutateAsync(variables, { onSuccess, onError });
  };

  const setOpenDrawer = (open: boolean, paramName: "success" | "fail"): void => {
    const search = {
      success: undefined,
      fail: undefined,
      ...params,
    };
    if (open) {
      search[paramName] = open;

      void router.navigate({
        to: ".",
        search,
      });
    } else if (params[paramName]) {
      router.history.back();
    }
  };

  const date = nextSchedule
    ? formatWithZonedDate(
        convertDateTimeIgnoringTimezone(nextSchedule.Agendamento.data),
        "dd/MM",
      )
    : "";
  const locationName = reservedSchedule?.Unidade.nomeLimpo ?? "";
  const successDrawerDescription = `O período garantido do dia ${date}, na unidade ${locationName}, foi cancelado.`;

  return (
    <>
      <HeaderRoot>
        <HeaderButton icon={Left} align="start" />
        <HeaderTitle title="Período garantido" align="center" />
      </HeaderRoot>
      <Page>
        <AsyncDataWrapper {...queryResult}>
          <div className="flex flex-col gap-6">
            <p className="font-medium text-lg">Cancelar período pontual</p>
            <p>
              Ao cancelar o período garantido, você perderá a garantida de disponibilidade
              de sala.
            </p>
            {hasLinkedSchedule ? (
              // TODO copy
              <p>
                Na agenda vinculada a este período, o horário deste dia será fechado e
                deixa de ficar disponível para agendamento.
              </p>
            ) : null}
            <AsyncDataWrapper {...queryResultCancelSimulation} fallback={<CpsSpinner />}>
              <ReservedScheduleCancelSimulationInfo
                tax={simulationTax}
                cost={simulationCost}
                locationName={reservedSchedule?.Unidade.nomeLimpo}
                dayOfWeek={reservedSchedule?.diaDaSemana}
                date={nextSchedule?.Agendamento.data}
                startTime={reservedSchedule?.horaInicio}
                endTime={reservedSchedule?.horaFim}
              />
            </AsyncDataWrapper>
            <p>
              Mais detalhes em{" "}
              <ReservedScheduleTermsOfUseUrl
                reservedScheduleType={nextSchedule?.codModalidadePeriodoGarantido}
                title="Termos e Condições de Uso"
              />
              .
            </p>
            <Button
              data-testid="botao-cancela-periodo-garantido-individual"
              onClick={() => {
                void handleReservedScheduleCancelAsync();
              }}
              loading={isPending}
            >
              Cancelar período pontual
            </Button>
          </div>
          <ReservedScheduleCancelSuccessDrawer
            description={successDrawerDescription}
            open={Boolean(params.success)}
            primaryButtonText="Ir para meu período"
            secondaryButtonText="Ir para agenda"
            primaryButtonAction={() => {
              router.history.go(-3);
            }}
            secondaryButtonAction={() => {
              void router.navigate({
                to: "/calendar",
              });
            }}
          />
          <ReservedScheduleCancelFailDrawer
            open={Boolean(params.fail)}
            setOpen={(open: boolean) => {
              setOpenDrawer(open, "fail");
            }}
          />
        </AsyncDataWrapper>
      </Page>
    </>
  );
};

export const Route = createFileRoute(
  "/settings/reserved-schedules/$reservedScheduleId/$nextReserveId/cancel",
)({
  component: ReservedScheduleIndividualCancelPage,
  validateSearch: reservedScheduleIndividualCancelPageSearchParamsSchema,
  parseParams: (params) => ({
    reservedScheduleId: z.int().parse(Number(params.reservedScheduleId)),
    nextReserveId: z.int().parse(Number(params.nextReserveId)),
  }),
  beforeLoad: async (opts) => {
    try {
      const data = await ensureQueryData(
        opts.context,
        ReservedScheduleIndividualCancelPageQuery,
        {
          codPeriodoGarantidoRecorrente: opts.params.reservedScheduleId,
          codPeriodoGarantido: opts.params.nextReserveId,
        },
      );

      if (!data.periodoGarantidoRecorrente?.ativo || !data.periodoGarantido?.ativo) {
        throw new Error("reserved schedule is not active");
      }
    } catch (_e) {
      redirect({
        to: "/settings/reserved-schedules",
        throw: true,
      });
    }
  },
});
